/**
 * `publication.outcome_code` → одна фраза человеческим языком.
 *
 * Источники: `00-integration.md` §Ключ 7.5 (таблица «Что произошло») и
 * `09-blockers-closed.md` §9.3 (коды, которые эта таблица получила дополнительно).
 * Набор кодов ОТКРЫТ: в `outcome_code` попадает в том числе `reason` из отчёта публикатора
 * (§9.3, таблица записи), поэтому схема `PublicationSchema` его и не перечисляет. Отсюда
 * главное требование: незнакомый код обязан давать фразу, а не пустой блок — «пусто»
 * человек читает как «система молчит», и это худшее, что может сообщить экран
 * несостоявшейся публикации.
 *
 * Коды здесь НЕ показываются в основной фразе (их место — технический след под сгибом),
 * кроме запасной ветки: там код — единственное, что мы знаем.
 */

import {
  AUTO_APPROVAL_OFF_OUTCOME,
  CHANNEL_NOT_OF_BRAND_OUTCOME,
  SENT_TO_WRONG_CHAT_OUTCOME,
} from '@smm/contracts/schemas';
import type { Publication } from '../../api/types.ts';

type Phrase = (detail: string | null) => string;

const plain =
  (text: string): Phrase =>
  () =>
    text;

/** Пояснение приклеивается только когда оно есть: «: null.» человеку читать незачем. */
const withDetail =
  (head: string, tail = ''): Phrase =>
  (detail) =>
    detail === null || detail.trim() === '' ? `${head}.${tail}` : `${head}: ${detail}.${tail}`;

const NOT_SENT = 'Пост не отправлен.';

const CONTENT_CHANGED = `Содержание поста изменилось после того, как вы его одобрили. ${NOT_SENT}`;
const KEY_REJECTED = `Ключ публикации не прошёл проверку. ${NOT_SENT}`;
const WRONG_CHANNEL =
  'Публикатор настроен на другой канал. Это ошибка конфигурации сервера, а не поста.';
const SLOT_WINDOW = 'Пост не был отправлен в отведённые пять минут после назначенного времени.';

export const OUTCOME_PHRASES: Readonly<Record<string, Phrase>> = {
  // ── исход попытки отправки (09 §9.3) ────────────────────────────────────
  TELEGRAM_REJECTED: withDetail('Telegram отклонил публикацию'),
  SEND_UNCERTAIN: plain('Исход отправки неизвестен.'),
  LEASE_EXPIRED_BEFORE_SEND: plain(
    `Публикатор не забрал пост в отведённые пять минут. ${NOT_SENT}`,
  ),
  LEASE_EXPIRED_WHILE_SENDING: plain('Отправка началась, но не завершилась.'),

  // ── решения бэкенда и джоба обслуживания (09 §9.3) ──────────────────────
  slot_missed: plain(`Слот публикации упущен: время вышло раньше, чем пост ушёл. ${NOT_SENT}`),
  stale_content: plain(`Содержание версии изменилось после выпуска ключа. ${NOT_SENT}`),
  orphan_no_key: plain(
    `Ключ публикации так и не был выпущен — работа снята джобом обслуживания. ${NOT_SENT}`,
  ),
  unscheduled: plain('Пост снят с расписания человеком.'),
  // Изоляция брендов и автоодобрение (2026-10-25, docs/ai-lab.md). Коды — из контрактов: их
  // пишет бэкенд, и второго написания слова здесь быть не должно.
  [CHANNEL_NOT_OF_BRAND_OUTCOME]: plain(
    `Канал, на который подписан пост, больше не принадлежит его бренду по конфигу сервера. ${NOT_SENT}`,
  ),
  // Подробность (номер сообщения и чат, откуда оно вернулось) — то, по чему пост найти: бот
  // его не удалит, номер в работу не записан, и «Пост есть в канале» со ссылкой на чужой
  // канал отказывает (docs/operations.md §10).
  [SENT_TO_WRONG_CHAT_OUTCOME]: withDetail(
    'Telegram вернул сообщение не из того канала, на который пост подписан',
    ' Удалите его в том канале вручную в Telegram и нажмите «Поста нет в канале».',
  ),
  [AUTO_APPROVAL_OFF_OUTCOME]: plain(
    `Автоодобрение выключили до отправки: пост вернулся на согласование. ${NOT_SENT}`,
  ),

  // ── ветка «неопределённый исход», закрытая человеком ───────────────────
  manual_confirmed: plain('Вы подтвердили, что пост есть в канале.'),
  manual_not_found: plain('Вы подтвердили, что поста в канале нет.'),

  // ── счастливый путь: код есть и у него, экран публикации им пользуется ──
  sent_verified: plain('Пост опубликован, ответ Telegram совпал с канонической формой.'),
  sent_with_deviation: plain(
    'Пост опубликован, но ответ Telegram разошёлся с канонической формой — сверьте текст в канале.',
  ),

  // ── отказы публикатора: `reason` уезжает в outcome_code дословно (§Ключ 7.5) ──
  TARGET_NOT_ALLOWED: plain(WRONG_CHANNEL),
  TARGET_ADAPTER_UNSUPPORTED: plain(WRONG_CHANNEL),
  BRAND_UNKNOWN: plain(WRONG_CHANNEL),
  // Замок 3 автоодобрения и закрепление черновика за брендом в журнале публикатора.
  AUTO_APPROVAL_NOT_ALLOWED: plain(
    `Публикатор не разрешает автоодобрение этого бренда: ключ без решения человека не принят. ${NOT_SENT}`,
  ),
  DRAFT_BRAND_MISMATCH: plain(
    `Публикатор уже знает этот черновик под другим брендом — ключ не принят. ${NOT_SENT}`,
  ),

  CONTENT_HASH_MISMATCH: plain(CONTENT_CHANGED),
  CONTENT_NOT_CANONICAL: plain(CONTENT_CHANGED),
  MEDIA_HASH_MISMATCH: plain(CONTENT_CHANGED),
  MEDIA_SIZE_MISMATCH: plain(CONTENT_CHANGED),
  STALE_CONTENT: plain(CONTENT_CHANGED),

  BAD_SIGNATURE: plain(KEY_REJECTED),
  MALFORMED_TOKEN: plain(KEY_REJECTED),
  UNKNOWN_KID: plain(KEY_REJECTED),
  UNKNOWN_KEY_VERSION: plain(KEY_REJECTED),
  PAYLOAD_SCHEMA: plain(KEY_REJECTED),
  PAYLOAD_UNREADABLE: plain(KEY_REJECTED),
  PAYLOAD_TRAILING_BYTES: plain(KEY_REJECTED),
  PAYLOAD_VERSION_MISMATCH: plain(KEY_REJECTED),
  ACTION_MISMATCH: plain(KEY_REJECTED),
  ISSUED_IN_FUTURE: plain(KEY_REJECTED),
  KEY_LIFETIME_EXCEEDED: plain(KEY_REJECTED),
  CANON_ALGO_MISMATCH: plain(KEY_REJECTED),
  CONTENT_SCHEMA: plain(KEY_REJECTED),
  BAD_CLAIM_SHAPE: plain(`Публикатор получил работу в неверной форме. ${NOT_SENT}`),

  SLOT_EXPIRED: plain(SLOT_WINDOW),
  SLOT_NOT_STARTED: plain(SLOT_WINDOW),
  SLOT_WINDOW_INVALID: plain(SLOT_WINDOW),

  TELEGRAM_LIMITS: withDetail('Пост не проходит ограничения Telegram', ' Нужна правка.'),
  MEDIA_GEOMETRY_INVALID: withDetail('Картинка не проходит ограничения Telegram', ' Нужна правка.'),
  MEDIA_MIME_MISMATCH: withDetail('Тип файла картинки не тот, что объявлен'),
  MEDIA_REF_MISSING: plain(`У версии нет ссылки на байты картинки. ${NOT_SENT}`),
  MEDIA_FETCH_FAILED: plain(`Публикатор не смог получить байты картинки. ${NOT_SENT}`),

  DAILY_CAP_HARD_HIT: withDetail('Дневной лимит канала уже исчерпан', ` ${NOT_SENT}`),
  DRAFT_ALREADY_PUBLISHED: plain('Этот пост уже был опубликован ранее.'),
  KEY_SUPERSEDED: plain('Ключ устарел — для этого поста был выпущен более новый.'),
  NONCE_ALREADY_USED: plain('Ключ устарел — для этого поста был выпущен более новый.'),

  JOURNAL_WRITE_FAILED: plain(`Публикатор не смог записать журнал до отправки. ${NOT_SENT}`),
  PAYLOAD_ASSEMBLY_FAILED: plain(`Публикатор не смог собрать запрос к Telegram. ${NOT_SENT}`),

  // ── отзыв публикации ────────────────────────────────────────────────────
  REVOKE_WINDOW_EXPIRED: plain('Окно 48 часов истекло — Telegram уже не удалит сообщение.'),
  REVOKE_NOTHING_PUBLISHED: plain('Отзывать нечего: номера опубликованного сообщения нет.'),
  MESSAGE_DELETE_FORBIDDEN: plain('Telegram не дал боту удалить это сообщение.'),
};

/**
 * Судьба ОТЗЫВА человеческим языком (G-001). Отдельно от `outcomePhrase`, потому что это
 * другая ось: `outcome_code` рассказывает про публикацию, а отзыв — про решение убрать её,
 * и у решения свой исход. Пока этой фразы не было, экран «опубликован» после протухшего
 * отзыва выглядел ровно так же, как без отзыва вовсе: человек нажал кнопку, подтвердил
 * удержанием, увидел «отзыв запрошен» — и больше ничего, при живом посте в канале.
 */
export function retractionPhrase(r: NonNullable<Publication['retraction']>): string {
  switch (r.state) {
    case 'open':
      return 'Отзыв запрошен: ждём публикатора. Пост пока в канале.';
    case 'done':
      return 'Публикатор удалил сообщение из канала.';
    case 'expired':
      return (
        'Отзыв НЕ состоялся: публикатор не забрал задание, окно ключа истекло. ' +
        'Пост остаётся в канале — нажмите «Отозвать» ещё раз.'
      );
    default:
      return r.outcome_code === null
        ? 'Отзыв НЕ состоялся. Пост остаётся в канале — можно попробовать ещё раз.'
        : `Отзыв НЕ состоялся (${r.outcome_code}). Пост остаётся в канале — ` +
            'можно попробовать ещё раз.';
  }
}

/**
 * Фраза по коду. Пустой строки не возвращает никогда — в этом весь смысл функции.
 *
 * `code === null` означает «работа есть, исход ещё не записан»: так выглядит работа в
 * `scheduled`/`leased`/`sending`, и молчать про это тоже нельзя.
 */
export function outcomePhrase(code: string | null, detail: string | null): string {
  if (code === null) {
    return 'Исход публикации ещё не записан.';
  }
  const known = OUTCOME_PHRASES[code];
  if (known !== undefined) return known(detail);
  return detail === null || detail.trim() === ''
    ? `Публикация не состоялась. Код: ${code}.`
    : `Публикация не состоялась. Код: ${code} — ${detail}.`;
}

/**
 * Отказ `signer_draft_brand_pinned`: журнал демона подписи закрепил черновик за другим брендом —
 * база и журнал демона разошлись (`SIGNER_DRAFT_BRAND_PINNED_REASON` контрактов). Экран и
 * строка черновика уже совпали, поэтому «обновите экран» здесь — неправда: дело оператора, и
 * он уже получил алерт. `notSigned` — что не случилось: «Ключ не выпущен», «Отзыв не подписан».
 */
export function signerPinnedText(message: string, notSigned: string): string {
  return (
    `${message}. ${notSigned}: журнал демона подписи закрепил этот черновик за другим брендом. ` +
    'Обновление экрана не поможет — сообщите администратору.'
  );
}
